import type { ReactNode, ButtonHTMLAttributes } from 'react';
import { CONTENT_STATUS_LABELS, CONTENT_STATUS_TONES, type ContentStatus } from '@kontentowo/config';

type Tone = 'neutral' | 'info' | 'warning' | 'success' | 'danger';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export function StatusBadge({ status }: { status: ContentStatus | string }) {
  const s = status as ContentStatus;
  const tone = CONTENT_STATUS_TONES[s] ?? 'neutral';
  const label = CONTENT_STATUS_LABELS[s] ?? status;
  return <Badge tone={tone}>{label}</Badge>;
}

export function Card({ title, actions, children, flush }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <div className="card">
      {(title || actions) && (
        <div className="card__header">
          {title && <div className="card__title">{title}</div>}
          <div style={{ flex: 1 }} />
          {actions}
        </div>
      )}
      <div className={`card__body${flush ? ' card__body--flush' : ''}`}>{children}</div>
    </div>
  );
}

export function Button({
  variant = 'default',
  size,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' | 'success' | 'danger' | 'ghost'; size?: 'sm' }) {
  return (
    <button className={`btn${variant !== 'default' ? ` btn--${variant}` : ''}${size === 'sm' ? ' btn--sm' : ''}`} {...rest}>
      {children}
    </button>
  );
}

export function Spinner({ label = 'Ładowanie…' }: { label?: string }) {
  return (
    <div className="loading">
      <span className="spinner" />
      {label}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div className="page-header__text">
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="btn-row">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon = '∅', title, hint }: { icon?: string; title: string; hint?: string }) {
  return (
    <div className="empty">
      <div className="empty__icon">{icon}</div>
      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{title}</div>
      {hint && <div style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export function ErrorAlert({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return <div className="alert alert--error">{message}</div>;
}

export function Pagination({ page, pageSize, total, onChange }: { page: number; pageSize: number; total: number; onChange: (page: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  const first = Math.max(1, Math.min(page - 2, pages - 4));
  const last = Math.min(pages, first + 4);
  return <div className="btn-row" style={{ justifyContent: 'center', marginTop: 12 }}>
    <Button size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>‹</Button>
    {Array.from({ length: last - first + 1 }, (_, i) => first + i).map((number) => <Button key={number} size="sm" variant={number === page ? 'primary' : 'default'} onClick={() => onChange(number)}>{number}</Button>)}
    <Button size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>›</Button>
  </div>;
}

export function Modal({
  title,
  open,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal${wide ? ' modal--wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div className="modal__title">{title}</div>
          <button className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </div>
  );
}

export function StatCard({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}
